import {NgModule} from '@angular/core';
import {DrawingImageUploadHelper} from '@application/helper/drawing/drawing-image-upload-helper';
import {CadCamService} from '@infrastructure/cad-cam/cad-cam.service';
import {ARTICLES} from '@infrastructure/http/article/articles';
import {HttpArticlesService} from '@infrastructure/http/article/http-articles.service';
import {AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {HttpAuthenticationService} from '@infrastructure/http/authentication/http-authentication.service';
import {COLOR_SETS} from '@infrastructure/http/color-set/color-sets';
import {HttpColorSetsService} from '@infrastructure/http/color-set/http-color-sets.service';
import {COLORS} from '@infrastructure/http/color/colors';
import {HttpColorsService} from '@infrastructure/http/color/http-colors.service';
import {COLORED_YARN_SETS} from '@infrastructure/http/colored-yarn-set/colored-yarn-sets';
import {HttpColoredYarnSetsService} from '@infrastructure/http/colored-yarn-set/http-colored-yarn-sets.service';
import {DOWNLOAD_URLS} from '@infrastructure/http/download-urls/download-urls';
import {HttpDownloadUrlsService} from '@infrastructure/http/download-urls/http-download-urls.service';
import {FINISHING_TEMPLATES} from '@infrastructure/http/finishing-template/finishing-templates';
import {HttpFinishingTemplatesService} from '@infrastructure/http/finishing-template/http-finishing-templates.service';
import {FINISHINGS} from '@infrastructure/http/finishing/finishings';
import {HttpFinishingsService} from '@infrastructure/http/finishing/http-finishings.service';
import {HttpMachineQualitiesService} from '@infrastructure/http/machine-quality/http-machine-qualities.service';
import {MACHINE_QUALITIES} from '@infrastructure/http/machine-quality/machine-qualities';
import {HttpProductConfigurationsService} from '@infrastructure/http/product-configuration/http-product-configuration.service';
import {PRODUCT_CONFIGURATIONS} from '@infrastructure/http/product-configuration/product-configuration.interface';
import {HttpDatasheetsService} from '@infrastructure/http/technical-datasheets/http-technical-datasheets.service';
import {TECHNICAL_DATASHEETS} from '@infrastructure/http/technical-datasheets/technical-datasheets';
import {HttpTexeditService} from '@infrastructure/http/texedit/http-texedit.service';
import {TEXEDIT} from '@infrastructure/http/texedit/texedit';
import {HttpWeaveStructuresService} from '@infrastructure/http/weave-structure/http-weave-structures.service';
import {WEAVE_STRUCTURES} from '@infrastructure/http/weave-structure/weave-structures';
import {HttpWeftColoredYarnSetsService} from '@infrastructure/http/weft-colored-yarn-set/http-weft-colored-yarn-sets.service';
import {WEFT_COLORED_YARN_SETS} from '@infrastructure/http/weft-colored-yarn-set/weft-colored-yarn-sets';
import {HttpYarnSetsService} from '@infrastructure/http/yarn-set/http-yarn-sets.service';
import {YARN_SETS} from '@infrastructure/http/yarn-set/yarn-sets';
import {ProductConfigurationOpenInCadcamDialogComponent} from '@presentation/components/product-configuration-open-in-cadcam/product-configuration-open-in-cadcam-dialog.component';
import {DuplicateNameComponent} from '@presentation/pages/texedit/drawing-library/add//duplicate-name/duplicate-name.component';
import {AddDrawingComponent} from '@presentation/pages/texedit/drawing-library/add/drawing/add-drawing.component';
import {DrawingLibraryCustomSettingsComponent} from '@presentation/pages/texedit/drawing-library/custom-settings/drawing-library-custom-settings.component';
import {AddDrawingFormComponent} from '@presentation/pages/texedit/drawing-library/drawing-form/add/add-drawing-form.component';
import {EditDrawingFormComponent} from '@presentation/pages/texedit/drawing-library/drawing-form/edit/edit-drawing-form.component';
import {DrawingLibraryDetailsComponent} from '@presentation/pages/texedit/drawing-library/drawing-library-details/drawing-library-details.component';
import {DrawingLibrarySharedModule} from '@presentation/pages/texedit/drawing-library/drawing-library-shared.module';
import {DrawingLibraryThumbnailsComponent} from '@presentation/pages/texedit/drawing-library/drawing-library-thumbnails/drawing-library-thumbnails.component';
import {DrawingLibraryComponent} from '@presentation/pages/texedit/drawing-library/drawing-library.component';
import {TexeditCardPageExternalApplicationComponent} from '@presentation/pages/texedit/texedit-card-page/texedit-card-page-external-application/texedit-card-page-external-application.component';
import {texeditRouting} from '@presentation/pages/texedit/texedit.routing';
import {TextileDataSharedModule} from '@presentation/pages/textile-data/textile-data-shared.module';
import {SharedModule} from '@shared/shared.module';
import {CanShowErrorForFormControlModule, CapitalFirstLetterPipe, GridUtilsModule} from '@vdw/angular-component-library';
import {AgGridModule} from 'ag-grid-angular';
import {SaveAsEpComponent} from './drawing-library/drawing-library-details/save-as-ep/save-as-ep.component';
import {AddProductConfigurationPageComponent} from './product-configuration/add/add-product-configuration-page.component';
import {DesignPatternPreviewDialogComponent} from './product-configuration/add/design-pattern-preview-dialog/design-pattern-preview-dialog.component';
import {SelectWeftColoredYarnSetComponent} from './product-configuration/add/select-weft-colored-yarn-set/select-weft-colored-yarn-set.component';
import {ProductConfigurationOverviewPageComponent} from './product-configuration/overview/product-configuration-overview-page.component';
import {ProductConfigurationSettingsComponent} from './product-configuration/settings/product-configuration-settings.component';
import {ProductConfigurationPlaceholderService} from './product-configuration/shared/product-configuration-placeholder.service';
import {TechnicalDatasheetDetailsComponent} from './technical-datasheet/technical-datasheet-details/technical-datasheet-details.component';
import {TechnicalDatasheetImagePreviewComponent} from './technical-datasheet/technical-datasheet-image-preview/technical-datasheet-image-preview.component';
import {TechnicalDatasheetListComponent} from './technical-datasheet/technical-datasheet-list/technical-datasheet-list.component';
import {TechnicalDatasheetThumbnailsComponent} from './technical-datasheet/technical-datasheet-thumbnails/technical-datasheet-thumbnails.component';
import {TechnicalDatasheetComponent} from './technical-datasheet/technical-datasheet.component';
import {TuftLinkPageComponent} from './tuft-link-page/tuft-link-page.component';

@NgModule({
  imports: [TextileDataSharedModule, texeditRouting, TextileDataSharedModule, SharedModule, DrawingLibrarySharedModule, CanShowErrorForFormControlModule, AgGridModule, GridUtilsModule],
  providers: [
    {provide: MACHINE_QUALITIES, useClass: HttpMachineQualitiesService},
    {provide: COLOR_SETS, useClass: HttpColorSetsService},
    {provide: COLORS, useClass: HttpColorsService},
    {provide: COLORED_YARN_SETS, useClass: HttpColoredYarnSetsService},
    {provide: YARN_SETS, useClass: HttpYarnSetsService},
    {provide: FINISHINGS, useClass: HttpFinishingsService},
    {provide: FINISHING_TEMPLATES, useClass: HttpFinishingTemplatesService},
    {provide: DOWNLOAD_URLS, useClass: HttpDownloadUrlsService},
    {provide: ARTICLES, useClass: HttpArticlesService},
    {provide: TECHNICAL_DATASHEETS, useClass: HttpDatasheetsService},
    {provide: PRODUCT_CONFIGURATIONS, useClass: HttpProductConfigurationsService},
    {provide: AUTHENTICATION, useClass: HttpAuthenticationService},
    {provide: WEFT_COLORED_YARN_SETS, useClass: HttpWeftColoredYarnSetsService},
    {provide: WEAVE_STRUCTURES, useClass: HttpWeaveStructuresService},
    {provide: TEXEDIT, useClass: HttpTexeditService},
    ProductConfigurationPlaceholderService,
    DrawingImageUploadHelper,
    CapitalFirstLetterPipe,
    CadCamService
  ],
  declarations: [
    TexeditCardPageExternalApplicationComponent,
    DrawingLibraryComponent,
    DrawingLibraryThumbnailsComponent,
    DrawingLibraryDetailsComponent,
    DrawingLibraryCustomSettingsComponent,
    AddDrawingComponent,
    AddDrawingFormComponent,
    EditDrawingFormComponent,
    SaveAsEpComponent,
    DuplicateNameComponent,
    TechnicalDatasheetComponent,
    TechnicalDatasheetThumbnailsComponent,
    TechnicalDatasheetListComponent,
    TechnicalDatasheetImagePreviewComponent,
    TechnicalDatasheetDetailsComponent,
    ProductConfigurationOverviewPageComponent,
    AddProductConfigurationPageComponent,
    SelectWeftColoredYarnSetComponent,
    ProductConfigurationOpenInCadcamDialogComponent,
    DesignPatternPreviewDialogComponent,
    ProductConfigurationSettingsComponent,
    TuftLinkPageComponent
  ]
})
export class TexeditModule {}
